import { useEffect, useState } from 'react';
import { api, type CreditSummary, type LedgerRow } from '../lib/api';
import { useCreditBalance } from '../lib/credits';
import { buys, fillDays, sayCount, topN, usedPercent } from '../lib/charts';
import { DayBars, Donut, NameBars } from './Charts';

// ---------------------------------------------------------------------------
// The Credits page (204, drawn 205): what the plan gives, what the workspace
// holds, what a month of it buys, where the credits went — by day, by model
// and by bench, as pictures and as tables — the packs at the plan's own
// rate, and the ledger. Reachable in Quick and Pro. A model's name in the
// tables opens its page in the catalogue.
// ---------------------------------------------------------------------------

const money = (cents: number, currency = 'usd') => new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const KIND_WORD: Record<LedgerRow['kind'], string> = { grant: 'Plan grant', purchase: 'Pack', debit: 'Spent', expiry: 'Lapsed', refund: 'Refund', adjust: 'Adjustment' };
const BENCH_WORD: Record<string, string> = { image: 'pictures', edit: 'edits', video: 'shots', music: 'beds', text: 'the director', tts: 'narration', upscale: 'enlargements', stt: 'transcription', render: 'rendered minutes' };
const PLURAL: Record<string, string> = { image: 'pictures', edit: 'edits', music: 'beds', upscale: 'enlargements', render: 'rendered minutes' };
const RANGES = [7, 30, 90] as const;
type Range = (typeof RANGES)[number];
type Example = { bench: string; modelName: string; variant: string; credits: number; unit: string };

const tierName = (t: string) => t.replace(/^studio_/, '').replace(/^\w/, (c) => c.toUpperCase());

export function Credits({ role, onOpenModel }: { role: string | null; onOpenModel?: (bench: string | null, modelId: string) => void }) {
  const { credits, reload } = useCreditBalance();
  const [range, setRange] = useState<Range>(30);
  const [summary, setSummary] = useState<CreditSummary | null>(null);
  const [examples, setExamples] = useState<Example[] | null>(null);
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canBuy = role === 'owner' || role === 'admin';

  useEffect(() => {
    api.creditLedger({ limit: 50 }).then((r) => { setRows(r.rows); setNext(r.next); }).catch((e: Error) => setError(e.message));
    api.plans().then((p) => setExamples(p.examples)).catch(() => setExamples([]));
    const q = new URLSearchParams(window.location.search);
    const flag = q.get('credits');
    if (flag) {
      setNotice(flag === 'success' ? 'Thank you — the credits land within a minute, once Stripe confirms the payment.' : 'Checkout was cancelled. Nothing changed.');
      q.delete('credits');
      window.history.replaceState({}, '', window.location.pathname + (q.toString() ? `?${q}` : '') + window.location.hash);
      setTimeout(reload, 5000);
    }
  }, [reload]);
  useEffect(() => {
    let live = true;
    setSummary(null);
    api.creditSummary(range).then((s) => { if (live) setSummary(s); }).catch((e: Error) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [range]);

  async function older() {
    if (!next) return;
    setBusy('older');
    try { const r = await api.creditLedger({ cursor: next, limit: 50 }); setRows((all) => [...all, ...r.rows]); setNext(r.next); }
    catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  }
  async function buy(packId: string) {
    setBusy(packId); setError(null);
    try { const r = await api.buyPack(packId); window.location.href = r.url; }
    catch (e) { setError((e as Error).message); setBusy(null); }
  }

  const grant = credits?.plan.monthlyCredits ?? 0;
  const pct = credits ? usedPercent(grant, credits.balance.plan) : 0;
  const days = summary ? fillDays(summary.byDay, range) : [];
  const spentInRange = summary ? summary.byModel.reduce((a, m) => a + m.credits, 0) : 0;
  type ModelBar = { id: string | undefined; label: string; sub: string | undefined; credits: number; units: number; bench: string | null };
  const byModel: ModelBar[] = summary ? topN<ModelBar>(summary.byModel.map((m) => ({ id: m.model.id, label: m.model.name, sub: m.bench ? BENCH_WORD[m.bench] ?? m.bench : undefined, credits: m.credits, units: m.units, bench: m.bench })), 8,
    (rest) => ({ id: undefined, label: `${rest.length} other models`, sub: undefined, credits: rest.reduce((a, r) => a + r.credits, 0), units: 0, bench: null })) : [];
  const byBench = summary ? summary.byBench.filter((b) => b.credits > 0).map((b) => ({ label: b.bench ? BENCH_WORD[b.bench] ?? b.bench : 'other', value: b.credits })) : [];

  return (
    <>
      <div className="vhead">
        <div>
          <h2>Credits</h2>
          <p>What the plan gives, what this workspace holds, what it buys, and where it went.</p>
        </div>
      </div>
      {error && <div className="err">{error}</div>}
      {notice && <div className="notice">{notice}</div>}

      <div className="plan-grid">
        <section className="card" data-tool="credits-plan">
          <h3>Your plan</h3>
          {!credits ? <p className="sub-dim">Loading…</p> : credits.unmetered ? (
            <p style={{ margin: 0 }}>Unmetered — this workspace is not charged credits.</p>
          ) : (
            <>
              <p className="plan-big">{tierName(credits.plan.tier)}</p>
              <dl className="kv">
                <dt>Each month</dt><dd><b>{sayCount(credits.plan.monthlyCredits)}</b> credits</dd>
                <dt>Your rate</dt><dd>{credits.plan.centsPer1000 ? `${money(credits.plan.centsPer1000)} a thousand` : '—'}{credits.plan.centsPer1000 ? <span className="sub-dim"> · packs cost the same</span> : null}</dd>
                <dt>Unspent credits</dt><dd>{credits.plan.rolloverDays ? `carry for ${credits.plan.rolloverDays} days` : 'lapse at the end of the month'}</dd>
                <dt>Renews</dt><dd>{new Date(credits.plan.renewsAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}</dd>
              </dl>
            </>
          )}
        </section>

        <section className="card" data-tool="credits-report">
          <h3>Balance</h3>
          {!credits ? <p className="sub-dim">Loading…</p> : credits.unmetered ? (
            <p style={{ margin: 0 }}>No balance to keep.</p>
          ) : (
            <div className="row wrap" style={{ gap: 14, alignItems: 'center' }}>
              {grant > 0 && (
                <Donut parts={[{ label: 'Used this month', value: Math.max(0, grant - credits.balance.plan) }, { label: 'Left from the plan', value: Math.max(0, credits.balance.plan) }]}
                       centre={{ big: `${pct}%`, small: 'used' }} />
              )}
              <dl className="kv" style={{ flex: '1 1 160px', minWidth: 0 }}>
                <dt>From the plan</dt><dd><b>{sayCount(credits.balance.plan)}</b></dd>
                <dt>Bought</dt><dd><b>{sayCount(credits.balance.purchased)}</b><span className="sub-dim"> · never expire</span></dd>
                <dt>Total</dt><dd><b>{sayCount(credits.balance.total)}</b></dd>
                <dt>Spent this month</dt><dd>{sayCount(credits.monthSpent)}</dd>
              </dl>
              {credits.expiring.length > 0 && (
                <p className="sub-dim" style={{ margin: 0, flexBasis: '100%' }}>
                  {credits.expiring.map((l) => `${sayCount(l.credits)} lapse on ${new Date(l.at).toLocaleDateString()}`).join(' · ')}
                </p>
              )}
            </div>
          )}
        </section>
      </div>

      {credits && !credits.unmetered && grant > 0 && examples && examples.length > 0 && (
        <section className="card" data-tool="credits-buys">
          <h3>What a month buys</h3>
          <p className="sub-dim" style={{ margin: 0 }}>Your {sayCount(grant)} credits, spent on one thing at the house's default model. Mix as you like.</p>
          <ul className="buys">
            {examples.filter((e) => e.credits > 0).map((e) => {
              const b = buys(grant, e.credits, e.unit);
              return (
                <li key={`${e.bench}-${e.variant}`}>
                  <span><b>{sayCount(b.count)}</b> {b.what || PLURAL[e.bench] || BENCH_WORD[e.bench] || e.bench}</span>
                  <span className="sub-dim">{e.modelName}{e.variant && e.variant !== 'each' && e.variant !== 'bed' ? ` · ${e.variant}` : ''}</span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {credits && !credits.unmetered && credits.packs.length > 0 && (
        <section className="card" data-tool="credits-packs">
          <h3>Buy credits</h3>
          <p className="sub-dim" style={{ margin: 0 }}>
            At your plan's rate{credits.plan.centsPer1000 ? ` — ${money(credits.plan.centsPer1000)} a thousand` : ''}. Bought credits never expire and are spent after the plan's.
          </p>
          <div className="row wrap" style={{ gap: 8 }}>
            {credits.packs.map((p) => (
              <button key={p.id} type="button" className="btn" disabled={!canBuy || !credits.billing || !p.ready || busy !== null} title={!canBuy ? 'Owners and admins buy credits' : !p.ready ? 'Not on sale yet — the price is being set up' : `${p.credits.toLocaleString()} credits`}
                      onClick={() => void buy(p.id)}>
                {busy === p.id ? 'Opening Stripe…' : `${p.credits.toLocaleString()} for ${money(p.amount, p.currency)}`}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="card" data-tool="credits-summary">
        <div className="row between wrap" style={{ alignItems: 'baseline', gap: 8 }}>
          <h3 style={{ margin: 0 }}>Where the credits went</h3>
          <div className="range" role="group" aria-label="Range">
            {RANGES.map((r) => <button key={r} type="button" className={r === range ? 'on' : ''} aria-pressed={r === range} onClick={() => setRange(r)}>{r} days</button>)}
          </div>
        </div>
        {!summary ? <p className="sub-dim">Loading…</p> : summary.byModel.length === 0 ? <p className="sub-dim" style={{ margin: 0 }}>Nothing spent in the last {range} days.</p> : (
          <>
            <p className="sub-dim" style={{ margin: 0 }}><b>{sayCount(spentInRange)}</b> credits over {range} days · about {sayCount(spentInRange / range)} a day</p>
            <DayBars rows={days} />
            <div className="row wrap" style={{ gap: 18, alignItems: 'flex-start' }}>
              <div style={{ flex: '2 1 300px', minWidth: 0 }}>
                <h4 className="lbl">By model</h4>
                <NameBars rows={byModel.map((m) => ({ id: m.id, label: m.label, sub: m.sub, value: m.credits }))}
                          onPick={onOpenModel ? (id) => onOpenModel(byModel.find((m) => m.id === id)?.bench ?? null, id) : undefined} />
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <h4 className="lbl">By bench</h4>
                <Donut parts={byBench} centre={{ big: sayCount(spentInRange), small: 'credits' }} />
              </div>
            </div>
            <details>
              <summary className="sub-dim">The same, as a table</summary>
              <div className="tablewrap">
                <table className="ledger">
                  <thead><tr><th>Model</th><th>Bench</th><th className="num">Units</th><th className="num">Credits</th></tr></thead>
                  <tbody>
                    {summary.byModel.map((m) => (
                      <tr key={`${m.model.id}-${m.bench}`}>
                        <td>{onOpenModel ? <button type="button" className="linkish" onClick={() => onOpenModel(m.bench, m.model.id)}>{m.model.name}</button> : m.model.name}</td>
                        <td>{m.bench}</td><td className="num">{m.units.toLocaleString()}</td><td className="num">{m.credits.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </>
        )}
      </section>

      <section className="card" data-tool="credits-ledger">
        <div className="row between" style={{ alignItems: 'baseline' }}>
          <h3 style={{ margin: 0 }}>Ledger</h3>
          <button type="button" className="btn tiny" onClick={() => void api.creditsCsv().catch((e: Error) => setError(e.message))}>Download CSV</button>
        </div>
        <div className="tablewrap">
          <table className="ledger">
            <thead><tr><th>When</th><th>What</th><th>Model</th><th className="num">Credits</th><th>Reference</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{when(r.at)}</td>
                  <td>{KIND_WORD[r.kind]}{r.bucket === 'purchased' ? ' · bought' : ''}</td>
                  <td>{r.model ? (onOpenModel && r.model.name ? <><button type="button" className="linkish" onClick={() => onOpenModel(r.bench, r.model!.id)}>{r.model.name}</button>{r.variant ? ` · ${r.variant}` : ''}</> : `${r.model.name}${r.variant ? ` · ${r.variant}` : ''}`) : r.note ?? ''}</td>
                  <td className={`num${r.credits < 0 ? ' neg' : ''}`}>{r.credits > 0 ? '+' : ''}{Math.round(r.credits).toLocaleString()}</td>
                  <td className="sub-dim">{r.ref}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="sub-dim">Nothing yet.</td></tr>}
            </tbody>
          </table>
        </div>
        {next && <button type="button" className="btn tiny" disabled={busy === 'older'} onClick={() => void older()}>{busy === 'older' ? 'Loading…' : 'Older'}</button>}
      </section>
    </>
  );
}
